import { Pencil, Trash2, UsersRound } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DataTable,
  NARROW_ONLY,
  SortableHeadCell,
  TABLE_ICON_CLASS,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeadCell,
  TableItem,
  TableRow,
} from "@/components/data-table";
import { FilterBar } from "@/components/filter-bar";
import { Highlight } from "@/components/highlight";
import { MultiSelect } from "@/components/multi-select";
import { RowActions } from "@/components/row-action";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { Person, Team } from "@/lib/api";
import { ACCESS_CHIP, CHIP, CHIP_TONE } from "@/lib/chips";
import { matchesSearch } from "@/lib/search";
import { type SortColumns, sortRows, useTableSort } from "@/lib/table-sort";
import { cn } from "@/lib/utils";

import {
  allMembers,
  type AppGrant,
  type GrantChip,
  grantChips,
  grantedApps,
  type GrantRow,
  membersPreview,
  teamAccessCount,
  teamHolds,
} from "./utils";

/** Team, members, access, actions. */
const COLUMNS = 4;

/** The team table's sortable columns. */
type TeamColumn = "team" | "members" | "access";

/** Each column's first order: names from A, the most members first, the most access first. */
const TEAM_SORT_COLUMNS: SortColumns<TeamColumn> = {
  team: "asc",
  members: "desc",
  access: "desc",
};

interface TeamTableProps {
  /** The team of everyone in no team, always first. */
  defaultTeam: Team;
  /** The Admins team, fixed, right after it. */
  adminsTeam: Team;
  /** The teams admins created, by name. */
  teams: readonly Team[];
  rows: readonly GrantRow[];
  apps: readonly AppGrant[];
  people: ReadonlyMap<string, Person>;
  /** How many known people are in no team: the default team's reach. */
  withoutTeam: number;
  onEdit: (team: Team) => void;
  onDelete: (team: Team) => void;
}

/**
 * Every team in one dense table, the default team first, then the Admins
 * team: its name and description (none for the default team, whose name says
 * it), its members (the first three, then "+n"), its access as chips (Lezen
 * light blue, Schrijven solid blue, with the group's title; then its apps in
 * grey) and its actions. The default team has no members and cannot be
 * deleted. The Admins team, named in the dashboard's language, has its
 * members by role and its extra members, "Alle toegang", and no delete
 * action. Every column sorts the teams admins created (the access by how
 * much it gives); the default team and the Admins team stay on top. A search
 * on the name and description, and the groups and apps a team holds, filter
 * every team. On a phone the members and the access move under the name, the
 * legend of the chips beside the column's title, and the actions into one
 * menu.
 */
export function TeamTable({
  defaultTeam,
  adminsTeam,
  teams,
  rows,
  apps,
  people,
  withoutTeam,
  onEdit,
  onDelete,
}: TeamTableProps) {
  const { t } = useTranslation();
  const sort = useTableSort({ columns: TEAM_SORT_COLUMNS, initial: null });
  const [search, setSearch] = useState("");
  const [holds, setHolds] = useState<string[]>([]);
  const filtered = search !== "" || holds.length > 0;
  const reset = () => {
    setSearch("");
    setHolds([]);
  };
  const matches = (team: Team, name: string, description: string | null) =>
    matchesSearch([name, description], search) && teamHolds(team, holds, rows);
  const showDefault = matches(defaultTeam, t("teams.defaultTeam"), null);
  const showAdmins = matches(adminsTeam, t("teams.adminsTeam"), t("teams.adminsTeamDetail"));
  const shown = sortRows(
    teams.filter((team) => matches(team, team.name, team.description ?? null)),
    sort.sort,
    (team, column) => {
      switch (column) {
        case "team":
          return team.name;
        case "members":
          return allMembers(team).length;
        case "access":
          return teamAccessCount(team, rows, apps);
      }
    },
  );
  const accessOptions = [
    ...rows.map((row) => ({ value: row.id, label: row.title })),
    ...apps.map((app) => ({ value: app.unit, label: app.title })),
  ];
  const accessLabel =
    holds.length === 0
      ? t("teams.filter.allAccess")
      : accessOptions
          .filter((option) => holds.includes(option.value))
          .map((option) => option.label)
          .join(", ");
  return (
    <div className="space-y-3">
      <FilterBar
        search={{ value: search, onChange: setSearch, label: t("teams.filter.search") }}
        columns="wide"
        filtered={filtered}
        onReset={reset}
      >
        <MultiSelect
          size="sm"
          className="w-full"
          options={accessOptions}
          value={holds}
          onChange={setHolds}
          label={accessLabel}
          name={t("teams.column.access")}
          allLabel={t("teams.filter.allAccess")}
        />
      </FilterBar>
      <DataTable label={t("people.tabs.teams")}>
        <TableHead>
          <SortableHeadCell
            {...sort.head("team")}
            className="md:min-w-44"
            // On a phone the access column leaves, so its legend comes here.
            after={<AccessLegend className={cn("ml-2 inline-flex align-middle", NARROW_ONLY)} />}
          >
            {t("teams.column.team")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("members")} narrow="hide">
            {t("teams.column.members")}
          </SortableHeadCell>
          <SortableHeadCell
            {...sort.head("access")}
            narrow="hide"
            after={<AccessLegend className="ml-2 hidden align-middle sm:inline-flex" />}
          >
            {t("teams.column.access")}
          </SortableHeadCell>
          <TableHeadCell align="right" className="w-px">
            <span className="sr-only">{t("teams.column.actions")}</span>
          </TableHeadCell>
        </TableHead>
        <tbody>
          {showDefault && (
            <TeamRow
              team={defaultTeam}
              name={t("teams.defaultTeam")}
              description={null}
              members={
                <span className="text-muted">
                  {t("teams.peopleWithoutTeam", { count: withoutTeam })}
                </span>
              }
              rows={rows}
              apps={apps}
              search={search}
              onEdit={onEdit}
            />
          )}
          {showAdmins && (
            <TeamRow
              team={adminsTeam}
              name={t("teams.adminsTeam")}
              description={t("teams.adminsTeamDetail")}
              members={<Members team={adminsTeam} people={people} />}
              rows={rows}
              apps={apps}
              search={search}
              onEdit={onEdit}
            />
          )}
          {shown.map((team) => (
            <TeamRow
              key={team.id}
              team={team}
              name={team.name}
              description={team.description ?? null}
              members={<Members team={team} people={people} />}
              rows={rows}
              apps={apps}
              search={search}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
          {!showDefault && !showAdmins && shown.length === 0 && (
            <TableEmptyRow colSpan={COLUMNS} onReset={reset}>
              {t("teams.filter.noMatch")}
            </TableEmptyRow>
          )}
        </tbody>
      </DataTable>
    </div>
  );
}

interface TeamRowProps {
  team: Team;
  name: string;
  description: string | null;
  members: ReactNode;
  rows: readonly GrantRow[];
  apps: readonly AppGrant[];
  /** The table's search, marked in the name and the description. */
  search: string;
  onEdit: (team: Team) => void;
  /** Absent for the default team and the Admins team, which cannot be deleted. */
  onDelete?: (team: Team) => void;
}

function TeamRow({
  team,
  name,
  description,
  members,
  rows,
  apps,
  search,
  onEdit,
  onDelete,
}: TeamRowProps) {
  const { t } = useTranslation();
  const chips = grantChips(team.grants, rows);
  const teamApps = grantedApps(team.grants, apps);
  return (
    <TableRow className="align-top">
      <TableCell>
        <TableItem
          icon={<UsersRound aria-hidden="true" strokeWidth={1.75} className={TABLE_ICON_CLASS} />}
          title={<Highlight text={name} query={search} />}
          summary={description && <Highlight text={description} query={search} />}
          facts={
            <>
              <p>{members}</p>
              <AccessChips chips={chips} apps={teamApps} all={team.all_access === true} />
            </>
          }
        />
      </TableCell>
      <TableCell narrow="hide">{members}</TableCell>
      <TableCell narrow="hide">
        <AccessChips chips={chips} apps={teamApps} all={team.all_access === true} />
      </TableCell>
      <TableCell align="right" className="px-1 py-1">
        <RowActions
          label={t("common.actionsFor", { name })}
          actions={[
            {
              label: t("teams.edit", { team: name }),
              short: t("common.edit"),
              icon: Pencil,
              onClick: () => onEdit(team),
            },
            // The default team and the Admins team cannot be deleted: their place stays empty,
            // so the edit buttons line up.
            onDelete
              ? {
                  label: t("teams.deleteFor", { team: name }),
                  short: t("common.delete"),
                  icon: Trash2,
                  destructive: true,
                  onClick: () => onDelete(team),
                }
              : null,
          ]}
        />
      </TableCell>
    </TableRow>
  );
}

/** What Lezen and Schrijven look like as chips: light blue and solid blue. */
function AccessLegend({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span aria-hidden="true" className={cn("gap-1", className)}>
      <span className={cn(CHIP, ACCESS_CHIP.read)}>{t("tools.access.read")}</span>
      <span className={cn(CHIP, ACCESS_CHIP.write)}>{t("tools.access.write")}</span>
    </span>
  );
}

interface AccessChipsProps {
  chips: readonly GrantChip[];
  /** The apps the team gets. */
  apps: readonly AppGrant[];
  /** The Admins team: one chip for everything instead. */
  all?: boolean;
}

/**
 * A team's access as chips: each group part it gets, by the group's title, in
 * its access's colour; then each app it gets, in grey. The Admins team's is
 * one "Alle toegang" chip, in solid blue.
 */
function AccessChips({ chips, apps, all = false }: AccessChipsProps) {
  const { t } = useTranslation();
  if (all) {
    return <span className={cn(CHIP, CHIP_TONE.write)}>{t("teams.allAccess")}</span>;
  }
  if (chips.length === 0 && apps.length === 0) {
    return <span className="text-muted text-xs">{t("teams.noAccess")}</span>;
  }
  return (
    <ul className="flex flex-wrap gap-1">
      {chips.map((chip) => {
        const text = t("teams.partTitle", {
          group: chip.title,
          access: t(`tools.access.${chip.access}`),
        });
        return (
          <Tooltip key={chip.unit}>
            <TooltipTrigger asChild>
              <li className={cn(CHIP, ACCESS_CHIP[chip.access], "max-sm:whitespace-normal")}>
                <span aria-hidden="true">{chip.title}</span>
                <span className="sr-only">{text}</span>
              </li>
            </TooltipTrigger>
            <TooltipContent>{text}</TooltipContent>
          </Tooltip>
        );
      })}
      {apps.map((app) => {
        const text = t("teams.apps.grant", { app: app.title });
        return (
          <Tooltip key={app.unit}>
            <TooltipTrigger asChild>
              <li className={cn(CHIP, CHIP_TONE.neutral, "max-sm:whitespace-normal")}>
                <span aria-hidden="true">{app.title}</span>
                <span className="sr-only">{text}</span>
              </li>
            </TooltipTrigger>
            <TooltipContent>{text}</TooltipContent>
          </Tooltip>
        );
      })}
    </ul>
  );
}

/**
 * A team's first three members by name (else email), then how many more; or
 * that it has none. On the Admins team the admins by role come first, and the
 * tooltip marks them.
 */
function Members({ team, people }: { team: Team; people: ReadonlyMap<string, Person> }) {
  const { t } = useTranslation();
  const members = allMembers(team);
  if (members.length === 0) {
    return <span className="text-muted">{t("teams.noMembers")}</span>;
  }
  const byRole = new Set(team.role_members ?? []);
  const stored = new Set(team.members);
  const { names, more } = membersPreview(members, people);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <span className="text-foreground">{names.join(", ")}</span>
          {more > 0 && (
            <span className="text-muted ml-1 whitespace-nowrap">
              {t("teams.moreMembers", { count: more })}
            </span>
          )}
          <span className="sr-only"> ({t("teams.memberCount", { count: members.length })})</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {members
          .map((email) =>
            !byRole.has(email)
              ? email
              : `${email} (${stored.has(email) ? t("teams.byRoleAndExtra") : t("teams.byRole")})`,
          )
          .join(", ")}
      </TooltipContent>
    </Tooltip>
  );
}
